import { useEffect, useRef, useState } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { useAccessEpoch } from '@/lib/access-epoch';
import { apiClient } from '@/lib/api-client';

/**
 * What the caller may do in Strumenti in the active organization (#387 PR-5, decision 100):
 * `GET /v1/access/my-permissions?module=tools`, resolved by the same decision function every route
 * uses. Read by the rail, the route gate and «Migliora con IA».
 *
 * ADVISORY, and it FAILS CLOSED: loading and error hold no permission, so nothing is offered until
 * the caller is known to hold it. The identity-documents server re-checks every route.
 */
export type ToolsPermissionsState =
  { status: 'loading' } | { status: 'ready'; permissions: readonly string[] } | { status: 'error' };

export const TOOLS_USE = 'tools:use';
export const TOOLS_AI_ENHANCE = 'tools:ai-enhance';

export function useToolsPermissions(): ToolsPermissionsState {
  const [state, setState] = useState<ToolsPermissionsState>({ status: 'loading' });
  // Permissions are held per organization: the answer for the previous one stops counting at the
  // switch, the same rule as `useCallerPermissions`.
  const [answeredFor, setAnsweredFor] = useState<string | undefined>(undefined);
  const context = useActiveContextOptional();
  const contextStatus = context?.status;
  const tenantId = context?.active?.tenantId;
  const organizationId = context?.active?.organizationId;
  // Decision 151: a change of access re-reads; the organization already answered keeps its answer
  // on screen meanwhile, and keeps it if the re-read fails.
  const epoch = useAccessEpoch();
  const answeredOrg = useRef<string | undefined>(undefined);

  useEffect(() => {
    const refresh = answeredOrg.current !== undefined && answeredOrg.current === organizationId;
    setAnsweredFor(organizationId);
    // The route resolves permissions in the active organization, so there is nothing to ask until
    // one is chosen; no organization at all holds no permission.
    if (contextStatus === 'loading') {
      setState({ status: 'loading' });
      return;
    }
    if (tenantId === undefined || organizationId === undefined) {
      setState({ status: 'ready', permissions: [] });
      return;
    }
    let active = true;
    if (!refresh) {
      answeredOrg.current = undefined;
      setState({ status: 'loading' });
    }
    apiClient
      .GET('/v1/access/my-permissions', {
        params: {
          query: { module: 'tools' },
          header: { 'X-Active-Tenant-Id': tenantId, 'X-Active-Organization-Id': organizationId },
        },
      })
      .then(({ data, error, response }) => {
        if (!active) return;
        if (error !== undefined || !response.ok || data === undefined) {
          if (!refresh) setState({ status: 'error' });
          return;
        }
        answeredOrg.current = organizationId;
        setState({ status: 'ready', permissions: data.permissions ?? [] });
      })
      .catch(() => {
        if (active && !refresh) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [contextStatus, tenantId, organizationId, epoch]);

  return answeredFor === organizationId ? state : { status: 'loading' };
}

export function holds(state: ToolsPermissionsState, permissionCode: string): boolean {
  return state.status === 'ready' && state.permissions.includes(permissionCode);
}
